import { CompanyMapper } from "@/adapters/Company.mapper";
import { TariffPairMapper } from "@/adapters/TariffPair.mapper";
import { zCompanyTariffResultDto } from "@/types/company/company.dto";
import { zCorpCompanyStatus } from "@/types/corp/corpSession.common";
import { zTariffPairRowDto } from "@/types/tariff/tariff-pair.dto";
import { toB2BError, toDataShapeError } from "@/utils/b2bErrors";
import { lazyService } from "@/utils/lazyMock";
import { supabase } from "@/utils/supabase";
/** Поле домена (dataIndex колонки) → колонка view. */
const SORT_COLUMNS = {
    name: "name",
    inn: "inn",
    status: "status",
    activeLinksCount: "active_links_count",
    createdAt: "created_at",
};
/** Символы, ломающие фильтр `or=(…)` PostgREST. */
const sanitizeSearch = (value) => value.replace(/[,()*%\\":]/g, " ").trim();
export class CompanyService {
    async getCompanies(params) {
        let query = supabase().from("v_corp_companies").select("*", { count: "exact" });
        const search = params.filters?.search ? sanitizeSearch(params.filters.search) : "";
        if (search) {
            query = query.or(`name.ilike.*${search}*,inn.ilike.*${search}*`);
        }
        const status = params.filters?.status;
        if (status && zCorpCompanyStatus.safeParse(status).success) {
            query = query.eq("status", status);
        }
        const sorters = Object.entries(params.sorters ?? {}).filter(([field, order]) => SORT_COLUMNS[field] && (order === "ascend" || order === "descend"));
        if (sorters.length > 0) {
            sorters.forEach(([field, order]) => {
                query = query.order(SORT_COLUMNS[field], { ascending: order === "ascend" });
            });
        }
        else {
            query = query.order("name", { ascending: true });
        }
        const from = (params.page - 1) * params.pageSize;
        query = query.range(from, from + params.pageSize - 1);
        const { data, error, count } = await query;
        if (error)
            throw toB2BError(error);
        try {
            return {
                data: (data ?? []).map(CompanyMapper.rowToDomain),
                total: count ?? 0,
                page: params.page,
                pageSize: params.pageSize,
            };
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
    async getCompany(companyId) {
        const { data, error } = await supabase()
            .from("v_corp_companies")
            .select("*")
            .eq("id", companyId)
            .maybeSingle();
        if (error)
            throw toB2BError(error);
        try {
            return data ? CompanyMapper.rowToDomain(data) : null;
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
    async getCompanyOptions() {
        const { data, error } = await supabase()
            .from("v_corp_companies")
            .select("id, name, status")
            .neq("status", "ARCHIVED")
            .order("name", { ascending: true });
        if (error)
            throw toB2BError(error);
        try {
            return (data ?? []).map((row) => ({
                id: String(row.id),
                name: String(row.name),
                status: zCorpCompanyStatus.parse(row.status),
            }));
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
    async createCompany(requisites) {
        const { data, error } = await supabase().rpc("corp_company_create", {
            p_payload: CompanyMapper.toCreatePayload(requisites),
        });
        if (error)
            throw toB2BError(error);
        return CompanyMapper.createResultToDomain(data);
    }
    async updateRequisites(companyId, requisites) {
        const { error } = await supabase().rpc("corp_company_update", {
            p_company_id: companyId,
            p_patch: CompanyMapper.toRequisitesPatch(requisites),
        });
        if (error)
            throw toB2BError(error);
    }
    async setStatus(companyId, status, reason) {
        const { data, error } = await supabase().rpc("corp_company_set_status", {
            p_company_id: companyId,
            p_status: status,
            p_reason: reason,
        });
        if (error)
            throw toB2BError(error);
        return CompanyMapper.setStatusResultToDomain(data);
    }
    async getTariffPair(companyId) {
        const { data, error } = await supabase()
            .from("v_corp_tariff_pairs")
            .select("*")
            .eq("corp_company_id", companyId)
            .or("is_deleted.is.null,is_deleted.eq.false")
            .eq("is_active", true)
            .limit(1)
            .maybeSingle();
        if (error)
            throw toB2BError(error);
        try {
            return data ? TariffPairMapper.rowToDomain(zTariffPairRowDto.parse(data)) : null;
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
    async upsertTariffPair(companyId, pair) {
        const { data, error } = await supabase().rpc("corp_tariff_pair_upsert", {
            p_company_id: companyId,
            p_payload: { advance: pair.advance, late: pair.late },
        });
        if (error)
            throw toB2BError(error);
        const result = zCompanyTariffResultDto.parse(data);
        return { idAdvance: result.idAdvance, idLateTariff: result.idLateTariff ?? null };
    }
}
// Литерал process.env.* подставляется при сборке: при REACT_APP_B2B_MOCK='false' (prod, G4) webpack
// выбрасывает ветку вместе с import() — мок не попадает в бандл (см. utils/lazyMock.ts).
export const companyService = process.env.REACT_APP_B2B_MOCK === "true"
    ? lazyService(() => import("@/services/mock/CompanyServiceMock").then((module) => module.companyServiceMock))
    : new CompanyService();
